import { useEffect, useState } from 'react';
import {
  ApiError,
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Page,
  Spinner,
  Stars,
  api,
  brl,
  dateTime,
  phone,
} from '@mobi/web';

interface UserRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: 'passenger' | 'driver' | 'admin';
  status: 'active' | 'suspended';
  rating: number | null;
  balanceCents: number;
  createdAt: string;
}

const ROLE_LABEL: Record<string, string> = { passenger: 'Passageiro', driver: 'Motorista', admin: 'Admin' };

export function Users() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState<'all' | 'passenger' | 'driver' | 'admin'>('all');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);

  async function search() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, role });
      const res = await api<{ users: UserRow[] }>(`/api/admin/users?${params}`);
      setUsers(res.users);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (openId !== null) {
    const user = users.find((u) => u.id === openId);
    if (user) return <UserDetail user={user} onBack={() => setOpenId(null)} onChanged={search} />;
  }

  return (
    <Page title="Usuários" subtitle="Busca, suspensão e carteira.">
      <Card className="mb-5">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Buscar">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="nome, e-mail ou telefone"
            />
          </Field>
          <Field label="Papel">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as typeof role)}
              className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3.5 text-base"
            >
              <option value="all">todos</option>
              <option value="passenger">passageiro</option>
              <option value="driver">motorista</option>
              <option value="admin">admin</option>
            </select>
          </Field>
        </div>
        <Button className="mt-4" onClick={search} loading={loading}>Buscar</Button>
      </Card>

      {loading ? (
        <div className="grid place-items-center py-16"><Spinner /></div>
      ) : users.length === 0 ? (
        <Empty title="Nada encontrado" />
      ) : (
        <Card className="divide-y divide-line p-0">
          {users.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => setOpenId(u.id)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-surface-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{u.name} <span className="text-faint">#{u.id}</span></p>
                <p className="truncate text-sm text-muted">{phone(u.phone)} · {u.email}</p>
              </div>
              <Badge tone="neutral">{ROLE_LABEL[u.role]}</Badge>
              {u.status === 'suspended' && <Badge tone="bad">suspenso</Badge>}
              <Stars value={u.rating} />
              <span className="w-24 shrink-0 text-right font-semibold">{brl(u.balanceCents)}</span>
            </button>
          ))}
        </Card>
      )}
    </Page>
  );
}

function UserDetail({
  user,
  onBack,
  onChanged,
}: {
  user: UserRow;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wallet, setWallet] = useState<{
    balanceCents: number;
    transactions: { id: number; amountCents: number; kind: string; description: string; createdAt: string; createdByName: string | null }[];
  } | null>(null);

  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<'refund' | 'promo' | 'adjustment'>('refund');
  const [description, setDescription] = useState('');

  async function loadWallet() {
    setWallet(await api(`/api/admin/users/${user.id}/wallet`));
  }

  useEffect(() => {
    void loadWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  async function toggleStatus() {
    setBusy(true);
    setError(null);
    try {
      const action = user.status === 'active' ? 'suspend' : 'reactivate';
      await api(`/api/admin/users/${user.id}/${action}`, { method: 'POST' });
      onChanged();
      onBack();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível concluir');
    } finally {
      setBusy(false);
    }
  }

  async function launch() {
    const cents = Math.round(Number(amount.replace(',', '.')) * 100);
    if (!cents || description.trim().length < 5) {
      setError('valor e descrição (mínimo 5 caracteres) são obrigatórios');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/users/${user.id}/wallet`, {
        body: { amountCents: cents, kind, description },
      });
      setAmount('');
      setDescription('');
      await loadWallet();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível lançar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title={user.name}>
      <button type="button" onClick={onBack} className="mb-4 text-sm text-muted underline underline-offset-4">
        ← voltar
      </button>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="space-y-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted">{ROLE_LABEL[user.role]} · {phone(user.phone)} · {user.email}</p>
              <p className="mt-1 text-sm text-faint">cadastrado em {dateTime(user.createdAt)}</p>
            </div>
            <Button
              variant={user.status === 'active' ? 'danger' : 'outline'}
              loading={busy}
              onClick={toggleStatus}
            >
              {user.status === 'active' ? 'Suspender' : 'Reativar'}
            </Button>
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <p className="font-semibold">Carteira</p>
            <p className="text-lg font-bold">{brl(wallet?.balanceCents ?? 0)}</p>
          </div>

          <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_auto_2fr_auto]">
            <Input placeholder="valor (R$)" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
              className="rounded-2xl border border-line bg-surface-2 px-4 py-3.5 text-base"
            >
              <option value="refund">estorno</option>
              <option value="promo">crédito promocional</option>
              <option value="adjustment">ajuste</option>
            </select>
            <Input placeholder="motivo (obrigatório)" value={description} onChange={(e) => setDescription(e.target.value)} />
            <Button onClick={launch} loading={busy}>Lançar</Button>
          </div>

          <div className="divide-y divide-line">
            {wallet?.transactions.length === 0 && <p className="py-3 text-sm text-faint">sem lançamentos</p>}
            {wallet?.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <p>{t.description}</p>
                  <p className="text-faint">{t.kind} · {dateTime(t.createdAt)} {t.createdByName && `· ${t.createdByName}`}</p>
                </div>
                <span className={t.amountCents >= 0 ? 'font-semibold text-good' : 'font-semibold text-danger'}>
                  {brl(t.amountCents)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Page>
  );
}
